<script setup lang="ts">
import { Platform, Folder, Watchlist, PlatformKey } from "../services/cmt";
import { platformColorPallette } from "@/config/colors";
import { useI18n } from "vue-i18n";
import { NavBarFeature } from "./NavBarPlatformFeature.vue";
import { useFeatureFlag } from "@/composables/useFeatureFlag";

const route = useRoute();
const i18n = useI18n();

const props = defineProps<{
  platform: Platform;
  isNavbarExpanded: boolean;
}>();

const mainColor = computed(() => platformColorPallette[props.platform.key].main);
const accentColor = computed(() => platformColorPallette[props.platform.key].accent);

const features = computed<NavBarFeature[]>(() => {
  const platformKey = props.platform.key;

  const featureEntries: [boolean | undefined, string, string, Folder[]?, Watchlist[]?][] = [
    [
      props.platform.features.comments?.comments,
      "comments",
      "Kommentare",
      props.platform.features.comments?.folders,
      props.platform.features.comments?.watchlists,
    ],
    [props.platform.features.dms, "dms", "DMs", []],
    [props.platform.features.tweets, "tweets", "Tweets", []],
    // Threads are hidden from the sidebar for now.
    // [
    //   props.platform.features.threads?.threads,
    //   "threads",
    //   "Threads",
    //   props.platform.features.threads?.folders,
    //   props.platform.features.threads?.watchlists,
    // ],
    [
      props.platform.features.posts?.postlist,
      "posts",
      "Posts",
      props.platform.features.posts?.folders,
      props.platform.features.posts?.watchlists,
    ],
    [
      props.platform.features.userList?.userlist,
      "userlist",
      "Userliste",
      props.platform.features.userList?.folders,
      props.platform.features.userList?.watchlists,
    ],
  ];

  return featureEntries
    .filter((item) => item[0])
    .map<NavBarFeature>(([_, key, label, folders, watchlists]) => ({
      key,
      label,
      url: `/${platformKey}/${key}/`,
      folders:
        folders?.map((folder) => ({
          label: i18n.t(`filters.${folder.name}`),
          key: folder.name,
          url: `/${platformKey}/${key}/${folder.name}`,
          filters: folder.filters,
        })) || [],
      watchlists:
        watchlists?.map((watchlist) => ({
          label: watchlist.name,
          key: `wl-${watchlist.id}`,
          isPublic: watchlist.isPublic,
          filters: {},
          url: `/${platformKey}/${key}/watchlists/${watchlist.id}`,
        })) || [],
    }));
});

const activeFeatureKey = computed(() => {
  if ((route.params as any).platformKey !== props.platform.key) return null;
  return route.path.split("/")[2];
});

const activePlatformKey = computed(() => {
  return (route.params as any).platformKey || null;
});

const isOpen = ref(props.platform.key === activePlatformKey.value);
</script>
<template>
  <div>
    <button
      class="group flex h-10 w-full cursor-pointer items-center justify-between font-extrabold tracking-widest disabled:opacity-50 disabled:cursor-not-allowed"
      :class="{ 'mb-6': !isOpen }"
      @click="isOpen = !isOpen"
      :aria-label="platform.label"
      role="treeitem" aria-level="1" :aria-expanded="isOpen" tabindex="0"
    >
      <HelpTooltipProvider placement="top" v-if="platform.key === activePlatformKey">
        <NavBarLabel
          :color="mainColor"
          :platform="platform.key"
          :label="platform.label"
          :active="platform.key === activePlatformKey"
          :iconName="platform.key"
          :isNavbarExpanded="isNavbarExpanded"
        />
        <template #content> {{ $t("general.tooltips.active_platform") }} </template>
      </HelpTooltipProvider>

      <NavBarLabel
        v-else
        :color="mainColor"
        :platform="platform.key"
        :label="platform.label"
        :active="platform.key === activePlatformKey"
        :iconName="platform.key"
        :isNavbarExpanded="isNavbarExpanded"
      />
      <img
        v-if="platform.key === activePlatformKey"
        :src="isOpen ? '/icons/active/arrow_down.svg' : '/icons/active/arrow_right.svg'"
        alt="arrow-right-icon"
        class="invisible group-hover:visible"
        aria-hidden="true"
      />
      <img
        v-else
        :src="isOpen ? '/icons/inactive/arrow_down.svg' : '/icons/inactive/arrow_right.svg'"
        alt="arrow-right-icon"
        class="invisible group-hover:visible"
        aria-hidden="true"
      />
    </button>
    <div role="group">
      <div
        v-show="isOpen"
        class="my-2 pl-1.5"
        :class="{ 'pl-7': isNavbarExpanded }"
        v-for="(feature, index) in features"
        :key="feature.key"
      >
        <NavBarPlatformFeature
          :mainColor="mainColor"
          :activeFeatureKey="activeFeatureKey"
          :feature="feature"
          :platformKey="platform.key"
          :isCurrentPlatformActive="activePlatformKey === platform.key"
          :isPlatformOpen="isOpen"
          :accentColor="accentColor"
          :isNavbarExpanded="isNavbarExpanded"
          role="treeitem"
          aria-level="2"
          :aria-setsize="features.length"
          :aria-posinset="(index + 1).toString()"
          tabindex="0"
        />
      </div>
    </div>
  </div>
</template>
<style scoped>
.router-link-exact-active {
  font-weight: 800;
  color: #042b5f;
  background-color: v-bind(accentColor);
}
</style>
